import React from 'react';

interface StatItem {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface StatsSectionProps {
  title?: string;
  subtitle?: string;
  stats: StatItem[];
  sectionStyle?: 'light' | 'dark' | 'gradient';
}

const StatsSection: React.FC<StatsSectionProps> = ({
  title,
  subtitle,
  stats,
  sectionStyle = 'light'
}) => {
  const bgClasses = {
    light: 'bg-white',
    dark: 'bg-gray-900',
    gradient: 'bg-gradient-to-r from-blue-600 to-indigo-600',
  };

  const textClasses = {
    light: 'text-gray-900',
    dark: 'text-white',
    gradient: 'text-white',
  };

  const subtextClasses = {
    light: 'text-gray-600',
    dark: 'text-white',
    gradient: 'text-blue-100',
  };

  const statValueClasses = {
    light: 'text-blue-600',
    dark: 'text-white',
    gradient: 'text-white',
  };

  const statLabelClasses = {
    light: 'text-gray-600',
    dark: 'text-white',
    gradient: 'text-blue-100',
  };

  const statBgClasses = {
    light: 'bg-white shadow-md',
    dark: 'bg-gray-800 shadow-md',
    gradient: 'bg-white/10 backdrop-blur-sm',
  };

  return (
    <section className={`py-16 md:py-24 ${bgClasses[sectionStyle]}`}>
      <div className="container mx-auto px-4 md:px-6">
        {(title || subtitle) && (
          <div className="text-center max-w-3xl mx-auto mb-16">
            {title && (
              <h2 className={`text-2xl md:text-4xl font-bold mb-4 ${textClasses[sectionStyle]}`}>
                {title}
              </h2>
            )}
            {subtitle && (
              <p className={`md:text-lg ${subtextClasses[sectionStyle]}`}>
                {subtitle}
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div 
              key={index} 
              className={`${statBgClasses[sectionStyle]} rounded-xl p-6 text-center`}
            >
              {stat.icon && (
                <div className="flex justify-center mb-4">
                  {stat.icon}
                </div>
              )}
              <div className={`text-3xl md:text-5xl font-bold mb-2 ${statValueClasses[sectionStyle]}`}>
                {stat.value}
              </div>
              <div className={`text-sm font-medium uppercase tracking-wider ${statLabelClasses[sectionStyle]}`}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;